import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { formatPrice } from "../lib/api";

const ColourwayCard = ({ colour, price = 18, onAdd, index = 0, totalCount = 12 }) => {
  const isDark = colour.slug === "blackout-white";
  const hasImage = Boolean(colour.image);
  const stock = typeof colour.stock === "number" ? colour.stock : 999;
  const soldOut = stock <= 0;
  const lowStock = !soldOut && stock <= 5;
  const isNewSeason = Boolean(colour.is_new_season);

  return (
    <article
      className="group border border-line bg-white relative"
      data-testid={`product-card-${colour.slug}`}
    >
      <Link
        to={soldOut ? "#" : `/product/pritch-grip-sock?c=${colour.slug}`}
        onClick={(e) => soldOut && e.preventDefault()}
        className={`block ${soldOut ? "pointer-events-none" : ""}`}
        aria-disabled={soldOut}
      >
        <div
          className="aspect-square relative overflow-hidden border-b border-line"
          style={{ backgroundColor: colour.hex }}
        >
          {hasImage ? (
            <img
              src={colour.image}
              alt={`Pritch grip sock — ${colour.name}`}
              className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${
                soldOut ? "grayscale opacity-40" : "group-hover:scale-[1.03]"
              }`}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p
                  className={`wordmark uppercase tracking-wider text-xl md:text-2xl ${
                    isDark ? "text-white" : "text-ink/90"
                  } ${soldOut ? "opacity-40" : ""}`}
                >
                  Pritch
                </p>
                <p
                  className={`mono text-[10px] tracking-widest mt-1 ${
                    isDark ? "text-white/70" : "text-ink/60"
                  }`}
                >
                  Grip Sock · {colour.name}
                </p>
              </div>
            </div>
          )}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
            <span className="mono text-[10px] tracking-widest uppercase px-2 py-1 border bg-white/85 backdrop-blur-sm border-ink/30 text-ink">
              {String(index + 1).padStart(2, "0")} / {String(totalCount).padStart(2, "0")}
            </span>
            {isNewSeason && !soldOut && (
              <span
                className="mono text-[10px] tracking-widest uppercase px-2 py-1 bg-ink text-pritch-volt-trim"
                data-testid={`new-season-${colour.slug}`}
              >
                New · {colour.season || "FW26"}
              </span>
            )}
          </div>
          <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white/85 backdrop-blur-sm border border-line px-2 py-1">
            <span
              className="w-3 h-3 border border-ink/30"
              style={{ backgroundColor: colour.hex }}
            />
            <span className="mono text-[10px] tracking-widest uppercase text-ink">
              {colour.name}
            </span>
          </div>
          {soldOut && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px]"
              data-testid={`sold-out-${colour.slug}`}
            >
              <span className="mono text-[11px] tracking-[0.22em] uppercase px-4 py-2 bg-ink text-white">
                Sold out
              </span>
            </div>
          )}
        </div>
        <div className="p-4 md:p-5">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[13px] uppercase tracking-widest font-semibold">
                {colour.name}
              </p>
              <p className="text-xs text-mute mt-1 line-clamp-2">{colour.description}</p>
              {lowStock && (
                <p
                  className="mono text-[10px] tracking-widest uppercase mt-2 text-ink"
                  data-testid={`low-stock-${colour.slug}`}
                >
                  Only {stock} left ·
                </p>
              )}
            </div>
            <span className="mono text-sm whitespace-nowrap">{formatPrice(price)}</span>
          </div>
        </div>
      </Link>
      <div className="px-4 md:px-5 pb-4 md:pb-5">
        <button
          type="button"
          onClick={() => !soldOut && onAdd && onAdd(colour)}
          disabled={soldOut}
          className={`w-full inline-flex items-center justify-between border px-3 py-2.5 text-[11px] tracking-[0.18em] uppercase font-bold transition-colors ${
            soldOut
              ? "border-line text-mute cursor-not-allowed"
              : "border-ink hover:bg-ink hover:text-white"
          }`}
          data-testid={`add-${colour.slug}`}
        >
          {soldOut ? "Sold out" : "Quick add"} {!soldOut && <ArrowRight size={14} />}
        </button>
      </div>
    </article>
  );
};

export default ColourwayCard;
